import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ROTATION_STRATEGIES = [
  { value: 'round_robin', label: 'Round robin', help: 'Cycle through mailboxes in order. Most predictable.' },
  { value: 'weighted', label: 'Weighted', help: 'Favour higher-weight mailboxes. Useful when some accounts have better deliverability.' },
  { value: 'least_used', label: 'Least used', help: 'Pick the mailbox with the lowest sent_today count. Best for even distribution.' },
];

function StatusDot({ status }) {
  const c = status === 'active' ? '#1a7f3c' : status === 'error' ? '#b91c1c' : status === 'paused' ? '#a16207' : '#888';
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, marginRight: 6 }} />;
}

export default function MailboxPools() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [pools, setPools] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [allMailboxes, setAllMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      // Pool picker should show ALL platform mailboxes, not just ones already
      // assigned to this client. Pool membership itself grants workspace access
      // (the workspace mailbox query unions assignments + pool members).
      const [pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
        fetch(`${API_BASE}/admin/mailboxes`, { headers }),
      ]);
      if (!pRes.ok) throw new Error(`Pools failed (${pRes.status})`);
      const poolsData = await pRes.json();
      setPools(poolsData);
      if (mRes.ok) {
        const mb = await mRes.json();
        setAllMailboxes(Array.isArray(mb) ? mb : mb.mailboxes || []);
      }
      if (poolsData.length > 0 && !selectedId) setSelectedId(poolsData[0].id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken, selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = pools.find(p => p.id === selectedId);

  if (isGlobalMode) {
    return (
      <div style={{ padding: 24 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 600 }}>Mailbox pools</h2>
        <p style={{ color: '#666', fontSize: 14 }}>
          Pools are managed per client. Open a client workspace to view or create pools.
        </p>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 24 }}>Loading pools…</div>;
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 110px)', overflow: 'hidden' }}>
      {/* Left rail — pool list */}
      <div style={{ width: 280, borderRight: '1px solid #EAE6E1', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #EAE6E1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Pools ({pools.length})</div>
          <button onClick={() => setCreating(true)} style={btnPrimarySm}>+ New</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {pools.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>No pools yet.</div>
          ) : pools.map(p => (
            <div
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              style={{
                padding: '12px 16px', cursor: 'pointer',
                borderLeft: p.id === selectedId ? '3px solid #8B1A1A' : '3px solid transparent',
                background: p.id === selectedId ? '#FBF1F1' : 'white',
                borderBottom: '1px solid #F5F2EE',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: '#111', marginBottom: 2 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#888' }}>
                {p.members?.length || 0} mailbox{(p.members?.length || 0) !== 1 ? 'es' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right pane — selected pool detail */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {selected ? (
          <PoolDetail
            pool={selected}
            allMailboxes={allMailboxes}
            clientId={clientId}
            isGlobalMode={isGlobalMode}
            onChange={load}
            onDeleted={() => { setSelectedId(null); load(); }}
            onAddMember={() => setAddingMember(true)}
          />
        ) : (
          <div style={{ color: '#888', padding: 32, textAlign: 'center' }}>
            {pools.length === 0 ? 'Create your first pool to group mailboxes for round-robin rotation.' : 'Select a pool to view details.'}
          </div>
        )}
      </div>

      {creating && (
        <CreatePoolModal
          clientId={clientId}
          allMailboxes={allMailboxes}
          onClose={() => setCreating(false)}
          onCreated={(newId) => {
            setCreating(false);
            setSelectedId(newId);
            load();
          }}
        />
      )}

      {addingMember && selected && (
        <AddMemberModal
          poolId={selected.id}
          allMailboxes={allMailboxes}
          existingIds={new Set(selected.members?.map(m => m.mailbox_id) || [])}
          onClose={() => setAddingMember(false)}
          onAdded={() => { setAddingMember(false); load(); }}
        />
      )}
    </div>
  );
}

function PoolDetail({ pool, allMailboxes, clientId, isGlobalMode, onChange, onDeleted, onAddMember }) {
  const { getToken } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pool.name);
  const [description, setDescription] = useState(pool.description || '');
  const [strategy, setStrategy] = useState(pool.rotation_strategy || 'round_robin');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    setName(pool.name);
    setDescription(pool.description || '');
    setStrategy(pool.rotation_strategy || 'round_robin');
    setEditing(false);
  }, [pool.id, pool.name, pool.description, pool.rotation_strategy]);

  const save = async () => {
    if (!name.trim()) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null, rotation_strategy: strategy }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setEditing(false);
      setSavedMsg('Saved');
      setTimeout(() => setSavedMsg(''), 2000);
      onChange();
    } catch (e) { alert(e.message); }
  };

  const removeMember = async (mailboxId, label) => {
    if (!window.confirm(`Remove ${label} from this pool?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}/members/${mailboxId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      onChange();
    } catch (e) { alert(e.message); }
  };

  const deletePool = async () => {
    if (!window.confirm(`Delete pool "${pool.name}"? Mailboxes themselves are kept; only the grouping is removed. Campaigns using only this pool will need a new pool or mailboxes assigned.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      onDeleted();
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          {editing ? (
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
              style={{ fontSize: 22, fontWeight: 600, border: '1px solid #D4CFC9', borderRadius: 5, padding: '4px 8px', width: '100%', maxWidth: 400 }} />
          ) : (
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{pool.name}</h2>
          )}
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Created {new Date(pool.created_at).toLocaleDateString('en-GB')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button onClick={save} style={btnPrimarySm}>Save</button>
              <button onClick={() => setEditing(false)} style={btnSecondarySm}>Cancel</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} style={btnSecondarySm}>Edit</button>
          )}
        </div>
      </div>

      {savedMsg && (
        <div style={{ background: '#E8F5EC', color: '#1a7f3c', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>{savedMsg}</div>
      )}

      <div style={{ background: 'white', border: '1px solid #EAE6E1', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={lblStyle}>Description</div>
            {editing ? (
              <input value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inp, width: '100%' }} placeholder="What is this pool for?" />
            ) : (
              <div style={{ fontSize: 14, color: '#111' }}>{pool.description || '—'}</div>
            )}
          </div>
          <div>
            <div style={lblStyle}>Member count</div>
            <div style={{ fontSize: 14, color: '#111' }}>{pool.members?.length || 0} mailbox{(pool.members?.length || 0) !== 1 ? 'es' : ''}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              Pools are saved sets of mailboxes for quick-adding to campaigns. Sends rotate automatically using least-used balancing.
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #EAE6E1', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #F0EDE9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
            Members ({pool.members?.length || 0})
          </div>
          <button onClick={onAddMember} style={btnPrimarySm}>+ Add mailboxes</button>
        </div>
        {(!pool.members || pool.members.length === 0) ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
            No mailboxes in this pool yet. Click "Add mailboxes" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee', color: '#666' }}>
                <th style={th}>Email</th>
                <th style={th}>Provider</th>
                <th style={th}>Status</th>
                <th style={th}>Daily cap</th>
                <th style={th}>Sent today</th>
                <th style={th}>Last used</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pool.members.map(m => (
                <tr key={m.mailbox_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={td}>
                    <Link to={`/clients/${clientId}/mail/mailboxes/${m.mailbox_id}`} style={{ color: '#8B1A1A', textDecoration: 'none' }}>
                      {m.email_address}
                    </Link>
                  </td>
                  <td style={{ ...td, textTransform: 'capitalize' }}>{m.provider}</td>
                  <td style={td}><StatusDot status={m.status} />{m.status || 'unknown'}</td>
                  <td style={td}>{m.daily_cap ?? '—'}</td>
                  <td style={td}>{m.sent_today ?? 0}</td>
                  <td style={{ ...td, color: '#888' }}>
                    {m.last_used_at ? new Date(m.last_used_at).toLocaleString('en-GB') : 'never'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => removeMember(m.mailbox_id, m.email_address)}
                      style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ background: 'white', border: '1px solid #EAE6E1', borderRadius: 8, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>Danger zone</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Delete this pool. The mailboxes themselves are kept and can be re-grouped.
        </div>
        <button onClick={deletePool} style={btnDangerSolid}>Delete pool</button>
      </div>
    </div>
  );
}

function CreatePoolModal({ clientId, allMailboxes, onClose, onCreated }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [strategy, setStrategy] = useState('round_robin');
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const filtered = allMailboxes.filter(m => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (m.email_address || '').toLowerCase().includes(q) || (m.display_name || '').toLowerCase().includes(q);
  });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const create = async () => {
    if (!name.trim()) { setError('Name required'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/pools`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          rotation_strategy: strategy,
          mailbox_ids: Array.from(selected),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Create failed (${res.status})`);
      }
      const data = await res.json();
      onCreated(data.pool_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...dialog, width: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 600 }}>New pool</h3>
        <div style={{ marginBottom: 14 }}>
          <label style={lblStyle}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. UK accountants outbound" style={{ ...inp, width: '100%' }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lblStyle}>Description (optional)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inp, width: '100%' }} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={lblStyle}>Mailboxes ({selected.size} selected)</label>
          <input type="text" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 6 }} />
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #EAE6E1', borderRadius: 6 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#888', fontSize: 13 }}>No mailboxes.</div>
            ) : filtered.map(m => {
              const checked = selected.has(m.id);
              return (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #F5F2EE', cursor: 'pointer', background: checked ? '#FBF1F1' : 'white' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(m.id)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>{m.email_address}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{m.provider} · {m.status}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={btnSecondary}>Cancel</button>
          <button onClick={create} disabled={saving || !name.trim()} style={{ ...btnPrimary, opacity: !name.trim() ? 0.5 : 1 }}>
            {saving ? 'Creating…' : 'Create pool'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddMemberModal({ poolId, allMailboxes, existingIds, onClose, onAdded }) {
  const { getToken } = useAuth();
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const available = allMailboxes.filter(m => !existingIds.has(m.id));
  const filtered = available.filter(m => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (m.email_address || '').toLowerCase().includes(q) || (m.display_name || '').toLowerCase().includes(q);
  });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const add = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      for (const id of selected) {
        const res = await fetch(`${API_BASE}/pools/${poolId}/members`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ mailbox_id: id }),
        });
        if (!res.ok) throw new Error(`Add ${id} failed (${res.status})`);
      }
      onAdded();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...dialog, width: 520 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 600 }}>Add mailboxes to pool</h3>
        <input type="text" placeholder="Search mailboxes…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 10 }} />
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #EAE6E1', borderRadius: 6, marginBottom: 12 }}>
          {available.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#888', fontSize: 13 }}>All mailboxes already in this pool.</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#888', fontSize: 13 }}>No matches.</div>
          ) : filtered.map(m => {
            const checked = selected.has(m.id);
            return (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #F5F2EE', cursor: 'pointer', background: checked ? '#FBF1F1' : 'white' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(m.id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13 }}>{m.email_address}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{m.provider} · {m.status}</div>
                </div>
              </label>
            );
          })}
        </div>
        {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#888' }}>{selected.size} selected</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={btnSecondary}>Cancel</button>
            <button onClick={add} disabled={saving || selected.size === 0} style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1 }}>
              {saving ? 'Adding…' : `Add ${selected.size || ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };
const inp = { padding: '8px 10px', border: '1px solid #D4CFC9', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
const th = { padding: '8px 12px', fontWeight: 500 };
const td = { padding: '8px 12px' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const dialog = { background: 'white', borderRadius: 10, padding: 24, maxWidth: '92vw', maxHeight: '88vh', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
const btnPrimary = { background: '#8B1A1A', color: 'white', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { background: 'white', color: '#333', border: '1px solid #D4CFC9', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimarySm = { ...btnPrimary, padding: '5px 10px', fontSize: 12 };
const btnSecondarySm = { ...btnSecondary, padding: '5px 10px', fontSize: 12 };
const btnDangerSolid = { background: '#b91c1c', color: 'white', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
